// Kompilacja konstrukcji (blueprint) do swiata Plancka.
// - wszystkie czesci korpusu => JEDNO dynamiczne body z wieloma fixtures
// - kazde kolo => osobne body + RevoluteJoint do korpusu
// - masa czesci jest rozdzielana miedzy jej ksztalty (bez podwojnego naliczania)

import * as planck from 'planck';
import { getDefinition } from '../domain/catalog';
import { resolveDrivetrain } from '../domain/shafts';
import { blueprintBoundsGrid, shapeAreaMeters2, shapeCentersWorldGrid } from '../domain/geometry';
import { METERS_PER_GRID, degreesToRadians, gridToMeters } from './units';
import type { PartDefinition, PartInstance, RobotBlueprint, Vec2Like } from '../domain/types';

/** Wspolny (ujemny) groupIndex: czesci robota nigdy nie koliduja ze soba. */
const ROBOT_GROUP = -1;

export interface PlacementOptions {
  /** Lewa krawedz robota na starcie (m). */
  startX: number;
  /** Wysokosc gornej powierzchni gruntu (m). */
  groundTopY: number;
  clearance?: number;
}

export interface ShaftMember {
  instance: PartInstance;
  definition: PartDefinition;
}

/**
 * Wal = JEDNO dynamiczne cialo obrotowe. Czlonek (kolo, zebatka, sprocket)
 * osadzony na wale jest jego fixture, wiec obraca sie RAZEM z walem - dlatego
 * widac ruch zebatek i lancuchow, a nie tylko kol.
 */
export interface ShaftHandle {
  /** Identyfikator = holderPartId#socketId. */
  id: string;
  /** Srodek osi walu w ukladzie blueprintu (jednostki klockowe). */
  grid: Vec2Like;
  body: planck.Body;
  joint: planck.RevoluteJoint;
  /** Czesci osadzone na wale (kola, zebatki, sprockety). */
  members: ShaftMember[];
  /** Czy wal ma droge do modulu napedu. */
  powered: boolean;
  /** Moment i predkosc bazowa - ustawione tylko na walach silnikow. */
  motor: { torque: number; speed: number } | null;
}

export interface WheelHandle {
  partId: string;
  definition: PartDefinition;
  /** Cialo/joint WALU, na ktorym osadzone jest kolo. */
  body: planck.Body;
  joint: planck.RevoluteJoint;
  driven: boolean;
  radiusMeters: number;
}

export interface RobotHandles {
  /** Origin blueprintu w swiecie. */
  chassisOrigin: planck.Vec2;
  chassis: planck.Body;
  chassisParts: { instance: PartInstance; definition: PartDefinition }[];
  shafts: ShaftHandle[];
  wheels: WheelHandle[];
  comWorld: planck.Vec2;
  boundsGrid: { minX: number; minY: number; maxX: number; maxY: number };
}

export interface WorldBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Origin blueprintu w swiecie, tak by konstrukcja stala nad gruntem bez penetracji. */
export function computeOrigin(blueprint: RobotBlueprint, options: PlacementOptions): planck.Vec2 {
  const bounds = blueprintBoundsGrid(blueprint);
  const clearance = options.clearance ?? 0.005;
  return new planck.Vec2(
    options.startX - gridToMeters(bounds.minX),
    options.groundTopY + clearance - gridToMeters(bounds.minY),
  );
}

export function compileRobot(
  world: planck.World,
  blueprint: RobotBlueprint,
  origin: planck.Vec2,
): RobotHandles {
  // Model walow decyduje, co nalezy do korpusu, a co wiruje na wale.
  const drivetrain = resolveDrivetrain(blueprint);
  const shaftOfMember = new Map<string, string>();
  for (const shaft of drivetrain.shafts) {
    for (const memberId of shaft.memberPartIds) shaftOfMember.set(memberId, shaft.id);
  }
  const byId = new Map(blueprint.parts.map((entry) => [entry.id, entry] as const));

  // Zasilanie: bez akumulatora silniki nie maja momentu. Akumulator byl dotad
  // tylko regula walidacji ("gotowosc"), a fizyka go ignorowala - stad niegotowy
  // pojazd (bez akumulatora) i tak jechal. Teraz brak zasilania = brak ruchu.
  const hasPower = blueprint.parts.some(
    (instance) => getDefinition(instance.typeId).category === 'battery',
  );

  const chassisParts: { instance: PartInstance; definition: PartDefinition }[] = [];
  for (const instance of blueprint.parts) {
    if (shaftOfMember.has(instance.id)) continue; // czlonek walu - osobne cialo obrotowe
    chassisParts.push({ instance, definition: getDefinition(instance.typeId) });
  }

  const chassis = world.createBody({
    type: 'dynamic',
    position: origin,
    linearDamping: 0,
    angularDamping: 0.05,
  });

  for (const { instance, definition } of chassisParts) {
    const totalAreaM2 = definition.shapes.reduce(
      (sum, shape) => sum + shapeAreaMeters2(shape, METERS_PER_GRID),
      0,
    );
    const density = totalAreaM2 > 0 ? definition.massKg / totalAreaM2 : 0;

    for (const entry of shapeCentersWorldGrid(instance, definition)) {
      const localCenter = new planck.Vec2(
        gridToMeters(entry.center.x),
        gridToMeters(entry.center.y),
      );
      const shape = entry.shape;
      const geometry =
        shape.kind === 'box'
          ? new planck.Box(
              gridToMeters(shape.w) / 2,
              gridToMeters(shape.h) / 2,
              localCenter,
              degreesToRadians(entry.angleDeg),
            )
          : new planck.Circle(localCenter, gridToMeters(shape.r));

      chassis.createFixture({
        shape: geometry,
        density,
        friction: 0.5,
        filterGroupIndex: ROBOT_GROUP,
      });
    }
  }

  // Kazdy WAL staje sie JEDNYM cialem obrotowym, a wszyscy jego czlonkowie
  // (kola, zebatki, sprockety) to jego fixtures - dlatego obracaja sie razem i
  // widac ruch zebatek oraz lancuchow, nie tylko kol.
  const joints = new Map<string, planck.RevoluteJoint>();
  const shafts: ShaftHandle[] = [];

  for (const shaft of drivetrain.shafts) {
    if (shaft.memberPartIds.length === 0) continue;

    const worldCenter = new planck.Vec2(
      origin.x + gridToMeters(shaft.grid.x),
      origin.y + gridToMeters(shaft.grid.y),
    );
    const body = world.createBody({ type: 'dynamic', position: worldCenter, angularDamping: 0 });

    const members: ShaftMember[] = [];
    for (const memberId of shaft.memberPartIds) {
      const instance = byId.get(memberId);
      if (!instance) continue;
      const definition = getDefinition(instance.typeId);
      members.push({ instance, definition });

      const totalAreaM2 = definition.shapes.reduce(
        (sum, shape) => sum + shapeAreaMeters2(shape, METERS_PER_GRID),
        0,
      );
      const density = totalAreaM2 > 0 ? definition.massKg / totalAreaM2 : 0;

      for (const entry of shapeCentersWorldGrid(instance, definition)) {
        // Fixture liczony wzgledem OSI walu (cialo stoi w srodku osi).
        const rx = entry.center.x - shaft.grid.x;
        const ry = entry.center.y - shaft.grid.y;
        const localCenter = new planck.Vec2(gridToMeters(rx), gridToMeters(ry));
        const shape = entry.shape;
        const geometry =
          shape.kind === 'box'
            ? new planck.Box(
                gridToMeters(shape.w) / 2,
                gridToMeters(shape.h) / 2,
                localCenter,
                degreesToRadians(entry.angleDeg),
              )
            : new planck.Circle(localCenter, gridToMeters(shape.r));

        body.createFixture({
          shape: geometry,
          density,
          friction: definition.wheel?.friction ?? 0.6,
          filterGroupIndex: ROBOT_GROUP,
        });
      }
    }

    // Motor ustawiamy tylko na wale silnika (reszta walow dostaje naped przez
    // zazebienia/lancuchy - GearJoint przenosi moment wg przelozenia). Bez
    // akumulatora zostaje `null` - pojazd nie ruszy.
    const motorLink = drivetrain.motors.find((entry) => entry.shaftId === shaft.id);
    let motor: { torque: number; speed: number } | null = null;
    if (motorLink && hasPower) {
      const drivePart = byId.get(motorLink.drivePartId);
      const drive = drivePart ? getDefinition(drivePart.typeId).drive : undefined;
      if (drive) motor = { torque: drive.maxMotorTorque, speed: -drive.targetAngularVelocity };
    }

    const joint = new planck.RevoluteJoint(
      {
        enableMotor: false, // naped wlaczamy po fazie osiadania (patrz simulation.ts)
        maxMotorTorque: motor ? motor.torque : 0,
        motorSpeed: motor ? motor.speed : 0,
      },
      chassis,
      body,
      worldCenter,
    );
    world.createJoint(joint);
    joints.set(shaft.id, joint);
    shafts.push({
      id: shaft.id,
      grid: shaft.grid,
      body,
      joint,
      members,
      // "Powered" opisuje realne zasilanie: bez akumulatora wal nie jest napedzany.
      powered: hasPower && drivetrain.ratios.has(shaft.id),
      motor,
    });
  }

  // Sprzezenia walow: zazebienia zebat (mesh) i lancuchy (chain/coupling).
  // W Plancka C = a1 + ratio*a2, czyli omega_to = -omega_from / ratio; znak
  // dobieramy tak, by kierunek byl zgodny z domena (mesh odwraca, chain nie).
  for (const link of drivetrain.links) {
    const joint1 = joints.get(link.fromShaftId);
    const joint2 = joints.get(link.toShaftId);
    if (!joint1 || !joint2) continue;
    world.createJoint(
      new planck.GearJoint({}, chassis, chassis, joint1, joint2, -link.direction * link.ratio),
    );
  }

  // Uchwyty kol - kazde odsyla do ciala/jointu swojego walu (naped, granice).
  const wheels: WheelHandle[] = [];
  for (const part of blueprint.parts) {
    const definition = getDefinition(part.typeId);
    if (definition.category !== 'wheel') continue;
    const shaft = shafts.find((entry) => entry.id === shaftOfMember.get(part.id));
    if (!shaft) continue;
    wheels.push({
      partId: part.id,
      definition,
      body: shaft.body,
      joint: shaft.joint,
      driven: shaft.powered,
      radiusMeters: gridToMeters(definition.wheel?.radiusGrid ?? 0.5),
    });
  }

  return {
    chassisOrigin: origin,
    chassis,
    chassisParts,
    shafts,
    wheels,
    comWorld: computeCenterOfMass(
      chassis,
      shafts.map((entry) => entry.body),
    ),
    boundsGrid: blueprintBoundsGrid(blueprint),
  };
}

/** Srodek masy calego robota: srednia wazona korpusu i cial walow (kola, zebatki). */
export function computeCenterOfMass(
  chassis: planck.Body,
  bodies: readonly planck.Body[],
): planck.Vec2 {
  let mass = chassis.getMass();
  let sumX = chassis.getWorldCenter().x * mass;
  let sumY = chassis.getWorldCenter().y * mass;
  for (const body of bodies) {
    const bodyMass = body.getMass();
    mass += bodyMass;
    sumX += body.getWorldCenter().x * bodyMass;
    sumY += body.getWorldCenter().y * bodyMass;
  }
  if (mass <= 0) return new planck.Vec2(0, 0);
  return new planck.Vec2(sumX / mass, sumY / mass);
}

/** Ciala walow robota (kola, zebatki, sprockety) - do srodka masy i granic. */
export function robotBodies(handles: RobotHandles): planck.Body[] {
  return handles.shafts.map((shaft) => shaft.body);
}

/** Rzeczywiste granice geometrii robota w swiecie (korpus + czesci na walach). */
export function robotBoundsWorld(handles: RobotHandles): WorldBounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const shape of [...placeChassisShapes(handles), ...placeShaftShapes(handles)]) {
    // Kazdy ksztalt ma wlasny kat (korpus albo wal) - liczymy mu obrys z osobna.
    const cos = Math.cos(shape.angle);
    const sin = Math.sin(shape.angle);
    const extentX = Math.abs(shape.halfX * cos) + Math.abs(shape.halfY * sin);
    const extentY = Math.abs(shape.halfX * sin) + Math.abs(shape.halfY * cos);
    minX = Math.min(minX, shape.centerX - extentX);
    maxX = Math.max(maxX, shape.centerX + extentX);
    minY = Math.min(minY, shape.centerY - extentY);
    maxY = Math.max(maxY, shape.centerY + extentY);
  }

  return { minX, minY, maxX, maxY };
}

export interface PlacedShape {
  kind: 'box' | 'circle';
  centerX: number;
  centerY: number;
  halfX: number;
  halfY: number;
  radius: number;
  /** Orientacja ksztaltu w swiecie (radiany). */
  angle: number;
  fill: string;
  stroke: string;
}

export interface PlacedPart {
  instance: PartInstance;
  definition: PartDefinition;
  /** Origin czesci w swiecie (metry). */
  originX: number;
  originY: number;
  /** Kat obrotu czesci w swiecie (radiany). */
  angle: number;
}

/**
 * Czesci korpusu wraz z ich umiejscowieniem w swiecie. Uzywane przez render,
 * zeby rysowac kazdy klocek osobno (z jego detalami), a nie anonimowe ksztalty.
 */
export function placeChassisParts(handles: RobotHandles): PlacedPart[] {
  const position = handles.chassis.getPosition();
  const angle = handles.chassis.getAngle();
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  return handles.chassisParts.map(({ instance, definition }) => {
    const localX = gridToMeters(instance.gridX);
    const localY = gridToMeters(instance.gridY);
    return {
      instance,
      definition,
      originX: position.x + localX * cos - localY * sin,
      originY: position.y + localX * sin + localY * cos,
      angle: angle + degreesToRadians(instance.rotation),
    };
  });
}

/** Ksztalty korpusu w ukladzie swiata (metry). Wspolne dla kolizji i renderu. */
export function placeChassisShapes(handles: RobotHandles): PlacedShape[] {
  const pos = handles.chassis.getPosition();
  const angle = handles.chassis.getAngle();
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const placed: PlacedShape[] = [];

  for (const { instance, definition } of handles.chassisParts) {
    for (const entry of shapeCentersWorldGrid(instance, definition)) {
      const lx = gridToMeters(entry.center.x);
      const ly = gridToMeters(entry.center.y);
      const centerX = pos.x + lx * cos - ly * sin;
      const centerY = pos.y + lx * sin + ly * cos;

      if (entry.shape.kind === 'box') {
        const swap = entry.angleDeg === 90 || entry.angleDeg === 270;
        placed.push({
          kind: 'box',
          centerX,
          centerY,
          halfX: gridToMeters(swap ? entry.shape.h : entry.shape.w) / 2,
          halfY: gridToMeters(swap ? entry.shape.w : entry.shape.h) / 2,
          radius: 0,
          angle,
          fill: definition.appearance.fill,
          stroke: definition.appearance.stroke,
        });
      } else {
        const radius = gridToMeters(entry.shape.r);
        placed.push({
          kind: 'circle',
          centerX,
          centerY,
          halfX: radius,
          halfY: radius,
          radius,
          angle: 0,
          fill: definition.appearance.fill,
          stroke: definition.appearance.stroke,
        });
      }
    }
  }

  return placed;
}

/**
 * Ksztalty czesci osadzonych na walach (kola, zebatki, sprockety) w ukladzie
 * swiata. Kazdy czlonek liczony wzgledem ciala SWOJEGO WALU, wiec obraca sie z
 * nim - dzieki temu render pokazuje ruch przekladni i lancuchow.
 */
export function placeShaftShapes(handles: RobotHandles): PlacedShape[] {
  const placed: PlacedShape[] = [];

  for (const shaft of handles.shafts) {
    const pos = shaft.body.getPosition();
    const angle = shaft.body.getAngle();
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    for (const { instance, definition } of shaft.members) {
      for (const entry of shapeCentersWorldGrid(instance, definition)) {
        const rx = gridToMeters(entry.center.x - shaft.grid.x);
        const ry = gridToMeters(entry.center.y - shaft.grid.y);
        const centerX = pos.x + rx * cos - ry * sin;
        const centerY = pos.y + rx * sin + ry * cos;

        if (entry.shape.kind === 'box') {
          const swap = entry.angleDeg === 90 || entry.angleDeg === 270;
          placed.push({
            kind: 'box',
            centerX,
            centerY,
            halfX: gridToMeters(swap ? entry.shape.h : entry.shape.w) / 2,
            halfY: gridToMeters(swap ? entry.shape.w : entry.shape.h) / 2,
            radius: 0,
            angle,
            fill: definition.appearance.fill,
            stroke: definition.appearance.stroke,
          });
        } else {
          const radius = gridToMeters(entry.shape.r);
          placed.push({
            kind: 'circle',
            centerX,
            centerY,
            halfX: radius,
            halfY: radius,
            radius,
            angle: 0,
            fill: definition.appearance.fill,
            stroke: definition.appearance.stroke,
          });
        }
      }
    }
  }

  return placed;
}

